import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { cellLabels } from "@constants/vulnerabilityPolicy";
import { getColorByVulnerabilityLevel } from "@utils/common";
import { capitalizeFirstLetter } from "@utils/common";

const VulnerabilityLevelOverview = ({ data = [] }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const maxItemsPerLevel = 16;

  // Get vulnerability codes for each level from actual data
  const getVulnerabilityCodesByLevel = (level) => {
    return data
      .filter(policy => policy.vulnerabilityLevel === parseInt(level))
      .map(policy => ({
        code: policy.levelCode,
        importanceLevelCd: policy.importanceLevelCd,
        weaknessCd: policy.weaknessCd,
        policy: policy
      }))
      .sort((a, b) => a.code.localeCompare(b.code)) // Sort alphabetically
      .slice(0, maxItemsPerLevel); // Limit to maximum 16 items
  };

  // Level configuration
  const levelConfig = [
    { level: "1", title: "Level 1" },
    { level: "2", title: "Level 2" },
    { level: "3", title: "Level 3" },
    { level: "4", title: "Level 4" }
  ];

  return (
    <div data-testid="vulnerability-level-overview-page" className={`rounded-2xl p-4 ${colors.bgPrimary} ${colors.borderPrimary} h-fit`}>
      <div className="mb-4">
        <h2 className={`text-lg font-semibold ${colors.textPrimary}`}>
          {t("vulnerability-policy.level-overview", "VULNERABILITY LEVEL OVERVIEW")}
        </h2>
      </div>

      {/* Matrix Layout - 4 levels, each with 2 rows of 8 items on large screens, 4 rows of 4 items on small screens */}
      <div className={`flex space-y-4 p-4 rounded-2xl ${colors.bgVulnMatrixPanel} 2xl:block justify-between`}>
        {levelConfig.map((config) => {
          const levelCodes = getVulnerabilityCodesByLevel(config.level);
          
          // Split codes into different layouts based on screen size
          // Large screens: 2 rows of 8 items each
          // Small screens: 4 rows of 4 items each
          const firstRow = levelCodes.slice(0, 8);
          const secondRow = levelCodes.slice(8, 16);
          
          // Small screen layout: 4 rows of 4 items
          const smallScreenRow1 = levelCodes.slice(0, 4);
          const smallScreenRow2 = levelCodes.slice(4, 8);
          const smallScreenRow3 = levelCodes.slice(8, 12);
          const smallScreenRow4 = levelCodes.slice(12, 16);

          return (
            <div key={config.level} className="space-y-2">
              {/* Large Screen Layout - 2 rows of 8 items (hidden on small screens) */}
              <div className="hidden 2xl:block space-y-2 [@media(min-width:1536px)_and_(max-width:1848px)]:hidden">
                {/* First Row - 8 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                    {config.level}
                  </div>
                  <div className="flex items-center space-x-1">
                    {/* Render first row codes */}
                    {firstRow.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;

                      const importanceLevelCd = codeData.importanceLevelCd;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      const titleContent = t(`vulnerability-policy.importance-level-${importanceLevelCd?.toLowerCase() || ''}`, importanceLevelCd) + " - " + weaknessCd;
                      
                      return (
                        <div
                          key={`${config.level}-lg-row1-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={ titleContent }
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots in first row */}
                    {Array.from({ length: Math.max(0, 8 - firstRow.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-lg-row1-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>

                {/* Second Row - 8 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                    {/* Empty space to align with first row */}
                  </div>
                  <div className="flex items-center space-x-1">
                    {/* Render second row codes */}
                    {secondRow.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      
                      return (
                        <div
                          key={`${config.level}-lg-row2-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={`${codeData.importanceLevelCd} - ${weaknessCd}`}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots in second row */}
                    {Array.from({ length: Math.max(0, 8 - secondRow.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-lg-row2-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Small Screen Layout - 4 rows of 4 items (hidden on large screens) */}
              <div className="2xl:hidden space-y-2 [@media(min-width:1536px)_and_(max-width:1848px)]:block">
                {/* Row 1 - 4 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                    {config.level}
                  </div>
                  <div className="flex items-center space-x-1">
                    {smallScreenRow1.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      
                      return (
                        <div
                          key={`${config.level}-sm-row1-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={`${codeData.importanceLevelCd} - ${weaknessCd}`}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots */}
                    {Array.from({ length: Math.max(0, 4 - smallScreenRow1.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-sm-row1-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 2 - 4 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                  </div>
                  <div className="flex items-center space-x-1">
                    {smallScreenRow2.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      
                      return (
                        <div
                          key={`${config.level}-sm-row2-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={`${codeData.importanceLevelCd} - ${weaknessCd}`}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots */}
                    {Array.from({ length: Math.max(0, 4 - smallScreenRow2.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-sm-row2-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 3 - 4 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                  </div>
                  <div className="flex items-center space-x-1">
                    {smallScreenRow3.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      
                      return (
                        <div
                          key={`${config.level}-sm-row3-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={`${codeData.importanceLevelCd} - ${weaknessCd}`}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots */}
                    {Array.from({ length: Math.max(0, 4 - smallScreenRow3.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-sm-row3-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>

                {/* Row 4 - 4 items */}
                <div className="flex items-center space-x-2">
                  <div className={`flex items-center justify-center w-8 ${colors.textStaticWhite} font-bold text-sm`}>
                  </div>
                  <div className="flex items-center space-x-1">
                    {smallScreenRow4.map((codeData, index) => {
                      const key = `${codeData.importanceLevelCd}_${codeData.weaknessCd}`;
                      const label = cellLabels[key]?.label || codeData.code;
                      const weaknessCd = capitalizeFirstLetter(codeData.weaknessCd);
                      
                      return (
                        <div
                          key={`${config.level}-sm-row4-${codeData.code}-${index}`}
                          className={`flex items-center justify-center w-8 h-8 rounded ${colors.textStaticWhite} font-bold text-sm`}
                          style={{ 
                            backgroundColor: getColorByVulnerabilityLevel(
                              parseInt(config.level)
                            )
                          }}
                          title={`${codeData.importanceLevelCd} - ${weaknessCd}`}
                        >
                          {label}
                        </div>
                      );
                    })}
                    
                    {/* Fill remaining slots */}
                    {Array.from({ length: Math.max(0, 4 - smallScreenRow4.length) }).map((_, index) => (
                      <div
                        key={`${config.level}-sm-row4-empty-${index}`}
                        className={`flex items-center justify-center w-8 h-8 rounded ${colors.bgVulnMatrixCellEmpty}`}
                      >
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VulnerabilityLevelOverview; 